import axios from "axios";
import { getTaskInfoForDescription } from "../../components/Tasks/tasks.service";
import { quote } from "../../config";
import { splitString } from "../../utils";

/**
 * Function to handle hovering over a link.
 */
export const handleLinkHover = async (
  target,
  cancelTokenRef,
  setSelectedLink,
  setUrl,
  setVisible,
  setTaskInfo,
  setEditFrameId,
  setHashPopupVisible,
  taskKey // Type of Editor
) => {
  cancelPreviousRequest(cancelTokenRef); // Cancel any ongoing requests to prevent race conditions
  hideEditorPopup(setVisible, setEditFrameId); // Hide editor popup if it's currently visible
  cancelTokenRef.current = axios.CancelToken.source();
  if (isAnchorTag(target)) {
    handleAnchorHover(target, setSelectedLink, setUrl, setVisible, setTaskInfo, setHashPopupVisible, cancelTokenRef, taskKey);
  }
  if (isTaskSpan(target)) {
    handleTaskSpanHover(target, setSelectedLink, setUrl, setVisible, setTaskInfo, setHashPopupVisible, cancelTokenRef, taskKey);
  }
  if (isIframe(target)) {
    handleIframeHover(target, setEditFrameId);
  }
  if (isImageInsideIframe(target)) {
    handleIframeImageHover(target, setEditFrameId);
  }
};

/**
 * Cancels the previous API request if one exists.
 * This prevents multiple overlapping API calls when hovering over elements.
 */
const cancelPreviousRequest = (cancelTokenRef) => {
  if (cancelTokenRef.current) {
    cancelTokenRef.current.cancel(quote?.OPERATION_CANCEL_MSG);
  }
};

/**
 * Hides the editor popup if it's currently open.
 */
const hideEditorPopup = (setVisible, setEditFrameId) => {
  const hovered = document.getElementById("editor-update-popup");
  if (hovered) {
    setVisible(false);
    setEditFrameId(null);
  }
};

/**
 * Checks if the target element is an anchor (<a>) tag.
 */
const isAnchorTag = (target) => target.closest("a");

/**
 * Handles hovering over an anchor (<a>) tag.
 * - Extracts the task ID from the URL if available.
 * - Fetches task details and displays the popup accordingly.
 */
const handleAnchorHover = async (target, setSelectedLink, setUrl, setVisible, setTaskInfo, setHashPopupVisible, cancelTokenRef, taskKey) => {
  let anchorTag = target.closest("a");
  if (!anchorTag) return;
  const url = new URL(anchorTag.href);
  const tId = url.searchParams.get("tId");
  // Set the selected link and URL
  setSelectedLink(anchorTag);
  setUrl(anchorTag.href);
  setVisible(false);
  setHashPopupVisible(false);
  if (tId) {
    // Fetch task details based on task ID
    const taskDetail = await getTaskInfoForDescription(tId, cancelTokenRef.current);
    setTaskInfo({ ...taskDetail, taskKey });
    setVisible(!!taskDetail); // Show popup only if task details exist
  } else {
    setTaskInfo(null);
    setVisible(true); // Show popup if no task ID exists
  }
};

/**
 * Checks if the target element is a span with a task ID.
 */
const isTaskSpan = (target) => target.localName === "span" && extractTaskId(target.id);

/**
 * Handles hovering over a task-related span.
 * - Extracts the task ID and fetches related task details.
 * - Displays the task information popup if data is found.
 */
const handleTaskSpanHover = async (target, setSelectedLink, setUrl, setVisible, setTaskInfo, setHashPopupVisible, cancelTokenRef, taskKey) => {
  const taskId = extractTaskId(target.id);
  setSelectedLink(target);
  setUrl(target.href);
  setHashPopupVisible(false);
  setVisible(false);
  // Fetch task details and update visibility accordingly
  const taskDetail = await getTaskInfoForDescription(taskId, cancelTokenRef.current);
  setTaskInfo({ ...taskDetail, taskKey });
  setHashPopupVisible(!!taskDetail);
};

/**
 * Checks if the target element is an iframe.
 */
const isIframe = (target) => target.localName === "iframe";

/**
 * Handles hovering over an iframe by setting the edit frame ID.
 */
const handleIframeHover = (target, setEditFrameId) => {
  setEditFrameId(target.id);
};

/**
 * Checks if the target element is an image inside an iframe.
 */
const isImageInsideIframe = (target) => target.localName === "img" && target.id.startsWith("iframe");

/**
 * Handles hovering over an image inside an iframe.
 */
const handleIframeImageHover = (target, setEditFrameId) => {
  setEditFrameId(target.id);
};

/**
 * Extracts the task ID from a string.
 * @param {string} str - The input string containing the task ID.
 * @returns {?string} The extracted task ID, or null if not found.
 */
function extractTaskId(str) {
  const parts = splitString(str, "_");
  const taskIdIndex = parts?.indexOf("task");
  if (taskIdIndex !== -1 && taskIdIndex < parts?.length - 1) {
    return parts[taskIdIndex + 1];
  } else {
    return null;
  }
}
